import React, {useCallback, useEffect, useId, useRef, useState} from 'react';
import {XIcon} from 'lucide-react';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE} from 'components/common/constants';
import {readStored, writeStored} from 'utils/localStorage.function';

export interface DrawerBounds {
    top: number;
    height: number;
}

export interface DrawerProps {
    open: boolean;
    onClose: () => void;
    label?: string;
    title?: string;
    closeAriaLabel: string;
    resizeAriaLabel?: string;
    footer?: React.ReactNode;
    dataCy?: string;
    // a fixed top and height in place of the viewport's full height
    bounds?: DrawerBounds | null;
    children: React.ReactNode;
}

// how wide the drawer can be dragged, and the step the arrow keys move its edge by
const WIDTH = Object.freeze({default: 420, min: 320, step: 20});
const maxWidth = () => Math.max(WIDTH.min, window.innerWidth - 320);
const clampWidth = (to: number) => Math.min(maxWidth(), Math.max(WIDTH.min, Math.round(to)));

// localStorage, not the store or sessionStorage, so the width outlives logout
export const WIDTH_STORAGE_KEY = '@@drawer/width';

const readStoredWidth = (): number => {
    const stored = Number(readStored(WIDTH_STORAGE_KEY));
    return stored > 0 ? clampWidth(stored) : WIDTH.default;
};

// the page beside the drawer sizes itself off this, so the width lives on the document root
const WIDTH_PROPERTY = '--app-drawer-width';

/**
 * Side sheet in Gaia's shape - Gaia has no drawer of its own yet, so this one is assembled from
 * its tokens and Tailwind utilities.
 *
 * Non-modal on purpose: no backdrop and no focus trap, so the page it opens over stays usable
 * while it is up. Escape closes it, and the focus goes back where it came from.
 */
export const Drawer = ({
                           open,
                           onClose,
                           label,
                           title,
                           closeAriaLabel,
                           resizeAriaLabel,
                           footer,
                           dataCy,
                           bounds,
                           children
                       }: DrawerProps): React.ReactElement => {

    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const titleId = useId();
    const [width, setWidth] = useState<number>(readStoredWidth);
    // Escape works for as long as the drawer is open, whoever hands onClose in
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    // returns the clamped width so callers can store what was actually applied
    const resize = useCallback((to: number) => {
        const clamped = clampWidth(to);
        setWidth(clamped);
        return clamped;
    }, []);

    // the width is published as a custom property rather than an inline style: the drawer takes
    // its own width from it, and so does whatever page is making room beside it
    useEffect(() => {
        document.documentElement.style.setProperty(WIDTH_PROPERTY, width + 'px');
        return () => {
            document.documentElement.style.removeProperty(WIDTH_PROPERTY);
        };
    }, [width]);

    // dragging the left edge: the listeners live on the document, so the pointer may leave the
    // handle - and the whole drawer - while the drag is on
    const startDrag = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        const fromRight = window.innerWidth - event.clientX - width;
        const body = document.body;
        let dragged = width;

        const onMouseMove = (moved: MouseEvent) => {
            dragged = resize(window.innerWidth - moved.clientX - fromRight);
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            writeStored(WIDTH_STORAGE_KEY, String(dragged));
            body.style.cursor = '';
            body.style.userSelect = '';
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        // the drag reads as a resize wherever the pointer goes, and nothing selects under it
        body.style.cursor = 'col-resize';
        body.style.userSelect = 'none';
    };

    const onHandleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        let to: number;
        if (event.key === 'ArrowLeft') to = width + WIDTH.step;
        else if (event.key === 'ArrowRight') to = width - WIDTH.step;
        else return;
        writeStored(WIDTH_STORAGE_KEY, String(resize(to)));
        event.preventDefault();
    };

    // the focus goes in when the drawer opens and back out when it closes - not on every render
    // of the page behind it
    useEffect(() => {
        if (!open) return;

        const openedFrom = document.activeElement as HTMLElement | null;
        closeButtonRef.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeRef.current();
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            // best effort: the control it was opened from may be gone by now
            openedFrom?.focus();
        };
    }, [open]);

    if (!open) return <></>;

    return (
        <div className={'aw:fixed aw:right-0 aw:z-[1050] aw:flex aw:flex-col' +
                (bounds ? '' : ' aw:top-0 aw:bottom-0') +
                ' aw:w-[var(--app-drawer-width,420px)] aw:max-w-[92vw] aw:border-l' +
                ' aw:border-[var(--ga-color-border-primary)] aw:bg-[var(--ga-color-surface-primary)]' +
                ' aw:shadow-[var(--ga-shadow-west)]'}
             style={bounds ? {top: bounds.top, height: bounds.height} : undefined}
             role="dialog"
             aria-label={title ? undefined : closeAriaLabel}
             aria-labelledby={title ? titleId : undefined}
             data-cy={dataCy}>
            {/* the left edge is the drag handle: wider than it looks, so it can be grabbed */}
            <div className={'aw:absolute aw:top-0 aw:bottom-0 aw:-left-[3px] aw:w-[6px]' +
                    ' aw:cursor-col-resize aw:bg-transparent' +
                    ' aw:hover:bg-[var(--ga-color-border-action)]' +
                    ' aw:focus-visible:bg-[var(--ga-color-border-action)] aw:focus-visible:outline-none'}
                 role="separator"
                 aria-orientation="vertical"
                 aria-label={resizeAriaLabel}
                 aria-valuenow={width}
                 tabIndex={0}
                 onMouseDown={startDrag}
                 onKeyDown={onHandleKeyDown}
                 data-cy="drawer.resize"/>
            <div className={'aw:flex aw:items-start aw:justify-between aw:gap-2 aw:p-4 aw:border-b' +
                    ' aw:border-[var(--ga-color-border-primary)]'}>
                <div className="aw:min-w-0">
                    {label &&
                        <div className={'aw:text-[11px] aw:uppercase aw:tracking-wide' +
                                ' aw:text-[var(--ga-color-text-body-secondary)]'}>
                            {label}
                        </div>
                    }
                    {title &&
                        <div className={'aw:text-base aw:font-semibold aw:break-words' +
                                ' aw:text-[var(--ga-color-text-headings)]'} id={titleId}>
                            {title}
                        </div>
                    }
                </div>
                <Button
                    type={ButtonType.GHOST}
                    iconOnly={true}
                    icon={<XIcon size={ICON_SIZE.MEDIUM}/>}
                    ariaLabel={closeAriaLabel}
                    dataCy="drawer.close"
                    ref={closeButtonRef}
                    onClick={onClose}
                />
            </div>
            <div className="aw:flex-1 aw:overflow-y-auto aw:p-4">{children}</div>
            {footer &&
                <div className={'aw:flex aw:gap-2 aw:px-4 aw:py-3 aw:border-t' +
                        ' aw:border-[var(--ga-color-border-primary)] aw:bg-[var(--ga-color-surface-page)]'}>
                    {footer}
                </div>
            }
        </div>
    );
};

export default Drawer;
